import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AiWorking } from "../../components/AiWorking";
import { FileDrop } from "../../components/FileDrop";
import { ChevronDown, ChevronUp, Sparkles, X } from "../../components/icons";
import type {
  DocumentTemplate,
  DocumentLayout,
  DocumentType,
  LayoutBlock,
  LayoutField,
  TemplateStandardField,
} from "../../lib/api";
import {
  activateLayout,
  disableLayout,
  fetchLayout,
  generateLayout,
  generateLayoutFromTemplate,
  layoutPreviewURL,
  saveLayoutDraft,
  SAMPLE_TYPES,
} from "../../lib/documentLayouts";
import { num, shortDate } from "../../lib/format";

const MAX_BYTES = 10 * 1024 * 1024;

const BLOCK_LABEL: Record<LayoutBlock["type"], string> = {
  national_header: "ຫົວເອກະສານ (ຕາແຜ່ນດິນ)",
  title: "ຊື່ເອກະສານ",
  paragraph: "ຫຍໍ້ໜ້າ",
  list: "ລາຍການ (-)",
  photo: "ກອບຮູບ 3 × 4",
  signatures: "ບ່ອນລົງລາຍເຊັນ",
  spacer: "ເວັ້ນແຖວ",
};

const KIND_LABEL: Record<LayoutField["kind"], string> = {
  text: "ຂໍ້ຄວາມສັ້ນ",
  longtext: "ຂໍ້ຄວາມຍາວ",
  number: "ຕົວເລກ",
  date: "ວັນທີ",
  select: "ເລືອກຈາກລາຍການ",
  phone: "ເບີໂທ",
};

const IMAGE_LABEL: Record<string, string> = {
  "": "ບໍ່ມີຮູບ",
  applicant_signature: "ລາຍເຊັນຜູ້ຮ້ອງ",
  signer_signature: "ລາຍເຊັນຜູ້ເຊັນ (ຕາແສງ)",
  office_seal: "ຕາປະທັບ",
};

type Tab = "fields" | "blocks";

/** บล็อกใหม่ที่ผู้ดูแลเพิ่มเอง — ค่าตั้งต้นผ่าน ValidateLayout ฝั่ง server (ช่องลงชื่อต้องมีอย่างน้อย 1 คอลัมน์) */
function newBlock(type: LayoutBlock["type"]): LayoutBlock {
  switch (type) {
    case "title":
      return { type, text: "ຊື່ເອກະສານ" };
    case "paragraph":
      return { type, text: "", align: "justify" };
    case "list":
      return { type, items: [""] };
    case "signatures":
      return {
        type,
        columns: [
          {
            title: "ຜູ້ຮ້ອງຂໍ",
            image: "applicant_signature",
            name: "{{applicant_name}}",
          },
        ],
      };
    default:
      return { type };
  }
}

/**
 * กรอบรูปวางก่อนชื่อเอกสาร (renderer จัดชื่อไว้ข้างรูปแบบแบบฟอร์ม A01) — ที่เหลือต่อท้าย
 * ยังย้ายขึ้น/ลงเองได้ทีหลัง
 */
function insertBlock(blocks: LayoutBlock[], block: LayoutBlock): LayoutBlock[] {
  if (block.type === "photo") {
    const at = blocks.findIndex((b) => b.type === "title");
    if (at >= 0) return [...blocks.slice(0, at), block, ...blocks.slice(at)];
  }
  return [...blocks, block];
}

/**
 * แม่แบบเอกสารจากไฟล์ตัวอย่าง (ADR-0028) — อัปโหลดตัวอย่าง → AI สร้างร่าง → ดูภาพตัวอย่างที่เติมข้อมูลสมมติ →
 * แก้ช่อง/ถ้อยคำได้ → ຢືນຢັນ ถึงใช้ออกเอกสารจริง (ระหว่างนั้นคำร้องใช้ทางเดิม)
 *
 * ข้อความเป็นบล็อก ไม่ใช่ HTML: แก้คำได้ ใส่ข้อมูลด้วย {{key}} — `canEdit` = super admin (server บังคับอีกชั้น)
 */
export function DocumentLayoutModal({
  doc,
  template,
  standardFields,
  canEdit,
  onClose,
}: {
  doc: DocumentType;
  /** แบบฟอร์ม PDF ที่อัปโหลดไว้แล้ว (ทางเดิม) — ใช้เป็นไฟล์ตัวอย่างได้เลย ไม่ต้องอัปโหลดซ้ำ */
  template?: DocumentTemplate;
  standardFields: TemplateStandardField[];
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["document-layout", doc.id],
    queryFn: () => fetchLayout(doc.id),
  });
  const layout = query.data ?? null;

  // ค่าที่ผู้ดูแลแก้ค้างอยู่ — null = ยังไม่แก้ (แสดงร่างจาก server ตรง ๆ)
  const [edits, setEdits] = useState<{
    blocks: LayoutBlock[];
    fields: LayoutField[];
    onePage: boolean;
  } | null>(null);
  const blocks = edits?.blocks ?? layout?.draft_blocks ?? [];
  const fields = edits?.fields ?? layout?.draft_fields ?? [];
  const onePage = edits?.onePage ?? layout?.draft_one_page ?? false;
  const dirty = edits !== null;
  const [tab, setTab] = useState<Tab>("fields");
  const [newType, setNewType] = useState<LayoutBlock["type"]>("photo");
  const [busy, setBusy] = useState<
    "" | "generate" | "save" | "activate" | "disable"
  >("");
  const [error, setError] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const hasDraft = (layout?.draft_blocks.length ?? 0) > 0;

  // ภาพตัวอย่างของร่าง — key ผูกกับ version: บันทึก/สร้างใหม่แล้ววาดใหม่เอง
  const preview = useQuery({
    queryKey: ["document-layout-preview", doc.id, layout?.version],
    queryFn: () => layoutPreviewURL(doc.id, "draft"),
    enabled: hasDraft,
    staleTime: 4 * 60 * 1000,
  });

  function setBlocks(update: (bs: LayoutBlock[]) => LayoutBlock[]) {
    setEdits((e) => ({
      blocks: update(e?.blocks ?? layout?.draft_blocks ?? []),
      fields: e?.fields ?? layout?.draft_fields ?? [],
      onePage: e?.onePage ?? layout?.draft_one_page ?? false,
    }));
  }
  function setFields(update: (fs: LayoutField[]) => LayoutField[]) {
    setEdits((e) => ({
      blocks: e?.blocks ?? layout?.draft_blocks ?? [],
      fields: update(e?.fields ?? layout?.draft_fields ?? []),
      onePage: e?.onePage ?? layout?.draft_one_page ?? false,
    }));
  }
  function setOnePage(value: boolean) {
    setEdits((e) => ({
      blocks: e?.blocks ?? layout?.draft_blocks ?? [],
      fields: e?.fields ?? layout?.draft_fields ?? [],
      onePage: value,
    }));
  }

  function accept(next: DocumentLayout) {
    queryClient.setQueryData(["document-layout", doc.id], next);
    setEdits(null);
  }

  async function run(kind: typeof busy, task: () => Promise<DocumentLayout>) {
    setBusy(kind);
    setError("");
    try {
      const next = await task();
      accept(next);
      if (kind === "activate" || kind === "disable") {
        void queryClient.invalidateQueries({ queryKey: ["document-types"] });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  function pickFile(f: File | null) {
    setError("");
    if (!f) {
      setFile(null);
      return;
    }
    if (!SAMPLE_TYPES.includes(f.type)) {
      setError("ໃຊ້ໄດ້ສະເພາະ PDF ຫຼື ຮູບ (PNG/JPEG/WebP)");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("ໄຟລ໌ໃຫຍ່ເກີນ 10 MB");
      return;
    }
    setFile(f);
  }

  function editBlock(i: number, patch: Partial<LayoutBlock>) {
    setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  }
  function moveBlock(i: number, d: -1 | 1) {
    setBlocks((bs) => {
      const j = i + d;
      if (j < 0 || j >= bs.length) return bs;
      const next = [...bs];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }
  function editField(i: number, patch: Partial<LayoutField>) {
    setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  }

  const status = !layout
    ? { cls: "status-badge--info", text: "ຍັງບໍ່ມີແມ່ແບບ" }
    : layout.active && !layout.draft_changed
      ? { cls: "status-badge--success", text: "ໃຊ້ງານຢູ່" }
      : layout.active
        ? {
            cls: "status-badge--pending",
            text: "ໃຊ້ງານຢູ່ · ມີຮ່າງໃໝ່ຍັງບໍ່ຢືນຢັນ",
          }
        : { cls: "status-badge--pending", text: "ຮ່າງ · ຍັງບໍ່ໄດ້ຢືນຢັນ" };

  const keys = [
    ...standardFields.map((f) => ({ key: f.key, label: f.label_lo })),
    ...fields.map((f) => ({ key: f.key, label: f.label_lo })),
  ];

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`ແມ່ແບບເອກະສານ ${doc.code}`}
    >
      <div className="modal modal--editor">
        <div className="modal__header">
          <div>
            <h2 className="modal__title">ແມ່ແບບເອກະສານ (AI)</h2>
            <p className="caption">
              <span className="num">{doc.code}</span> · {doc.name_lo} ·{" "}
              <span className={`status-badge ${status.cls}`}>
                {status.text}
              </span>
              {layout?.activated_at ? (
                <>
                  {" "}
                  · ເປີດໃຊ້{" "}
                  <span className="num">{shortDate(layout.activated_at)}</span>
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            className="btn-outline"
            onClick={onClose}
            aria-label="ປິດ"
          >
            <X size={16} />
          </button>
        </div>

        {error ? (
          <div className="alert-error">
            {layoutErrorLo(error)}
            {layoutErrorLo(error) !== error ? (
              <details className="caption" style={{ marginTop: "var(--space-1)" }}>
                <summary>ລາຍລະອຽດສຳລັບຜູ້ພັດທະນາ</summary>
                {error}
              </details>
            ) : null}
          </div>
        ) : null}

        <div className="layout-editor">
          <div className="layout-editor__preview">
            {busy === "generate" ? (
              <AiWorking title="AI ກຳລັງອ່ານໄຟລ໌ຕົວຢ່າງ ແລະ ສ້າງແມ່ແບບ…" />
            ) : preview.isFetching ? (
              <p className="caption layout-editor__hint">ກຳລັງວາດຕົວຢ່າງ…</p>
            ) : preview.data ? (
              <img src={preview.data} alt={`ຕົວຢ່າງ ${doc.name_lo}`} />
            ) : (
              <p className="caption layout-editor__hint">
                ອັບໂຫຼດໄຟລ໌ຕົວຢ່າງຂອງເອກະສານ (PDF ຫຼື ຮູບ) — AI ຈະສ້າງແມ່ແບບ ແລະ
                ລາຍການຊ່ອງທີ່ຜູ້ຮ້ອງຕ້ອງກອກໃຫ້
                ແລ້ວສະແດງຕົວຢ່າງຢູ່ບ່ອນນີ້ກ່ອນຢືນຢັນ
              </p>
            )}
          </div>

          <div className="layout-editor__panel stack">
            {canEdit ? (
              <div className="card stack">
                <div className="form-field-label">ໄຟລ໌ຕົວຢ່າງ</div>
                {template ? (
                  <button
                    type="button"
                    className="btn-outline"
                    disabled={busy !== ""}
                    onClick={() =>
                      void run("generate", () =>
                        generateLayoutFromTemplate(doc.id),
                      )
                    }
                  >
                    {busy === "generate"
                      ? "AI ກຳລັງສ້າງ…"
                      : `ໃຊ້ແບບຟອມ PDF ທີ່ອັບໂຫຼດໄວ້ (${template.file_name || "ແບບຟອມ.pdf"})`}
                  </button>
                ) : null}
                {template ? (
                  <p className="caption">ຫຼື ອັບໂຫຼດໄຟລ໌ອື່ນ:</p>
                ) : null}
                {layout?.sample_name ? (
                  <p className="caption">
                    ໄຟລ໌ປັດຈຸບັນ: {layout.sample_name} · ອັບເດດ{" "}
                    <span className="num">{shortDate(layout.updated_at)}</span>
                  </p>
                ) : null}
                <FileDrop
                  accept={SAMPLE_TYPES.join(",")}
                  file={file}
                  hint="PDF ຫຼື ຮູບ (PNG/JPEG/WebP) · ບໍ່ເກີນ 10 MB"
                  disabled={busy !== ""}
                  onPick={pickFile}
                />
                <button
                  type="button"
                  className="btn-outline"
                  disabled={!file || busy !== ""}
                  onClick={() => {
                    if (!file) return;
                    void run("generate", () => generateLayout(doc.id, file));
                  }}
                >
                  {busy === "generate" ? (
                    <>
                      <span className="btn-spinner" aria-hidden="true" /> AI ກຳລັງສ້າງ…
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />{" "}
                      {hasDraft ? "ໃຫ້ AI ສ້າງໃໝ່ຈາກໄຟລ໌ນີ້" : "ໃຫ້ AI ສ້າງແມ່ແບບ"}
                    </>
                  )}
                </button>
                {hasDraft ? (
                  <p className="caption">
                    ສ້າງໃໝ່ຈະແທນຮ່າງປັດຈຸບັນ —
                    ແມ່ແບບທີ່ໃຊ້ງານຢູ່ບໍ່ປ່ຽນຈົນກວ່າຈະກົດຢືນຢັນ
                  </p>
                ) : null}
              </div>
            ) : null}

            {hasDraft ? (
              <>
                <div className="card stack">
                  <label
                    className="row"
                    style={{ alignItems: "center", gap: "var(--space-2)" }}
                  >
                    <input
                      type="checkbox"
                      checked={onePage}
                      disabled={!canEdit || busy !== "" || !layout}
                      onChange={(e) => {
                        // ติ๊กแล้วบันทึก + วาดตัวอย่างใหม่ทันที (รวมการแก้อื่นที่ค้างอยู่ด้วย) — ไม่ให้ค่าหาย
                        // ตอนกดสร้างใหม่หรือปิดจอโดยไม่ได้กดบันทึก
                        const next = e.target.checked;
                        setOnePage(next);
                        if (!layout) return;
                        void run("save", () =>
                          saveLayoutDraft(
                            doc.id,
                            blocks,
                            fields,
                            next,
                            layout.version,
                          ),
                        );
                      }}
                    />
                    <span className="form-field-label" style={{ margin: 0 }}>
                      ບີບໃຫ້ພໍດີໜ້າດຽວ
                    </span>
                  </label>
                  <p className="caption">
                    ເນື້ອໃນຍາວເກີນໜ້າ ລະບົບຈະຫຍໍ້ທັງໃບໃຫ້ຈົບໃນໜ້າດຽວ
                    (ຫຍໍ້ໄດ້ສູງສຸດ 60% — ຍາວກວ່ານັ້ນຈະຂຶ້ນໜ້າໃໝ່). ບໍ່ເລືອກ =
                    ຂຶ້ນໜ້າໃໝ່ຕາມປົກກະຕິ
                  </p>
                </div>
                <div className="tab-group" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "fields"}
                    className={
                      tab === "fields" ? "tab-btn is-active" : "tab-btn"
                    }
                    onClick={() => setTab("fields")}
                  >
                    ຊ່ອງທີ່ຜູ້ຮ້ອງກອກ (
                    <span className="num">{num(fields.length)}</span>)
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "blocks"}
                    className={
                      tab === "blocks" ? "tab-btn is-active" : "tab-btn"
                    }
                    onClick={() => setTab("blocks")}
                  >
                    ຂໍ້ຄວາມໃນເອກະສານ
                  </button>
                </div>

                {tab === "fields" ? (
                  <div className="stack layout-editor__list">
                    <p className="caption">
                      ຊ່ອງເຫຼົ່ານີ້ຈະເປັນຟອມກອກໃນແອັບ — ຊື່, ທີ່ຢູ່, ເລກທີ,
                      ວັນທີ ແລະ ລາຍເຊັນ ລະບົບເຕີມເອງ ບໍ່ຕ້ອງມີໃນລາຍການ
                    </p>
                    {fields.length === 0 ? (
                      <p className="caption">ບໍ່ມີຊ່ອງທີ່ຕ້ອງກອກເພີ່ມ</p>
                    ) : null}
                    {fields.map((f, i) => (
                      <div key={f.key} className="card layout-field">
                        <div className="caption num">{`{{${f.key}}}`}</div>
                        <input
                          className="input-field"
                          value={f.label_lo}
                          disabled={!canEdit}
                          aria-label="ຊື່ຊ່ອງ"
                          onChange={(e) =>
                            editField(i, { label_lo: e.target.value })
                          }
                        />
                        <div className="row">
                          <select
                            className="input-field"
                            value={f.kind}
                            disabled={!canEdit}
                            aria-label="ຊະນິດ"
                            onChange={(e) =>
                              editField(i, {
                                kind: e.target.value as LayoutField["kind"],
                              })
                            }
                          >
                            {Object.entries(KIND_LABEL).map(([k, label]) => (
                              <option key={k} value={k}>
                                {label}
                              </option>
                            ))}
                          </select>
                          <label className="row caption">
                            <input
                              type="checkbox"
                              checked={f.required}
                              disabled={!canEdit}
                              onChange={(e) =>
                                editField(i, { required: e.target.checked })
                              }
                            />
                            ບັງຄັບ
                          </label>
                        </div>
                        {f.kind === "select" ? (
                          <>
                            <input
                              className="input-field"
                              value={(f.options ?? []).join(", ")}
                              disabled={!canEdit}
                              placeholder="ຕົວເລືອກ ຂັ້ນດ້ວຍ ,"
                              onChange={(e) =>
                                editField(i, {
                                  options: e.target.value
                                    .split(",")
                                    .map((o) => o.trim())
                                    .filter(Boolean),
                                })
                              }
                            />
                            <label className="row caption">
                              <input
                                type="checkbox"
                                checked={f.checkboxes ?? false}
                                disabled={!canEdit}
                                onChange={(e) =>
                                  editField(i, { checkboxes: e.target.checked })
                                }
                              />
                              ພິມທຸກຕົວເລືອກເປັນຊ່ອງໝາຍ ແລ້ວໝາຍອັນທີ່ເລືອກ
                            </label>
                          </>
                        ) : null}
                        <input
                          className="input-field"
                          value={f.section ?? ""}
                          disabled={!canEdit}
                          placeholder="ຫົວຂໍ້ (ແບ່ງໜ້າໃນແອັບ)"
                          onChange={(e) =>
                            editField(i, { section: e.target.value })
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="stack layout-editor__list">
                    <details>
                      <summary
                        className="caption"
                        style={{ cursor: "pointer" }}
                      >
                        ໃສ່ຂໍ້ມູນດ້ວຍ {"{{key}}"} — ເບິ່ງລາຍການ key
                      </summary>
                      <ul className="layout-keys">
                        {keys.map((k) => (
                          <li key={k.key}>
                            <span className="num">{`{{${k.key}}}`}</span>{" "}
                            {k.label}
                          </li>
                        ))}
                      </ul>
                    </details>
                    {blocks.map((b, i) => (
                      <div key={i} className="card layout-block">
                        <div
                          className="row"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="form-field-label">
                            {BLOCK_LABEL[b.type]}
                          </span>
                          {canEdit ? (
                            <span className="row">
                              <button
                                type="button"
                                className="link-action"
                                onClick={() => moveBlock(i, -1)}
                                aria-label="ຂຶ້ນ"
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                type="button"
                                className="link-action"
                                onClick={() => moveBlock(i, 1)}
                                aria-label="ລົງ"
                              >
                                <ChevronDown size={14} />
                              </button>
                              <button
                                type="button"
                                className="link-action"
                                onClick={() => {
                                  setBlocks((bs) =>
                                    bs.filter((_, j) => j !== i),
                                  );
                                }}
                              >
                                ລຶບ
                              </button>
                            </span>
                          ) : null}
                        </div>
                        {b.type === "title" || b.type === "paragraph" ? (
                          <textarea
                            className="input-field"
                            rows={b.type === "paragraph" ? 3 : 1}
                            value={b.text ?? ""}
                            disabled={!canEdit}
                            onChange={(e) =>
                              editBlock(i, { text: e.target.value })
                            }
                          />
                        ) : null}
                        {b.type === "paragraph" ? (
                          <textarea
                            className="input-field"
                            rows={1}
                            value={b.right ?? ""}
                            disabled={!canEdit}
                            placeholder="ຂໍ້ຄວາມເບື້ອງຂວາ ແຖວດຽວກັນ (ບໍ່ມີ ປ່ອຍວ່າງ)"
                            onChange={(e) =>
                              editBlock(i, { right: e.target.value })
                            }
                          />
                        ) : null}
                        {b.type === "paragraph" ? (
                          <select
                            className="input-field"
                            value={b.align ?? ""}
                            disabled={!canEdit}
                            aria-label="ຈັດວາງ"
                            onChange={(e) =>
                              editBlock(i, {
                                align: e.target.value as LayoutBlock["align"],
                              })
                            }
                          >
                            <option value="">ຊ້າຍ</option>
                            <option value="center">ກາງ</option>
                            <option value="right">ຂວາ</option>
                            <option value="justify">ເຕັມແຖວ</option>
                          </select>
                        ) : null}
                        {b.type === "paragraph" ? (
                          <label className="row caption">
                            <input
                              type="checkbox"
                              checked={b.indent ?? false}
                              disabled={!canEdit}
                              onChange={(e) =>
                                editBlock(i, { indent: e.target.checked })
                              }
                            />
                            ຫຍໍ້ໜ້າແຖວທຳອິດ
                          </label>
                        ) : null}
                        {b.type === "paragraph" || b.type === "list" ? (
                          <select
                            className="input-field"
                            value={b.level ?? 0}
                            disabled={!canEdit}
                            aria-label="ລະດັບຫົວຂໍ້"
                            onChange={(e) =>
                              editBlock(i, { level: Number(e.target.value) })
                            }
                          >
                            <option value={0}>ຫົວຂໍ້ຫຼັກ (ບໍ່ຂະຫຍັບເຂົ້າ)</option>
                            <option value={1}>ຂໍ້ຍ່ອຍ ຂັ້ນ 1</option>
                            <option value={2}>ຂໍ້ຍ່ອຍ ຂັ້ນ 2</option>
                            <option value={3}>ຂໍ້ຍ່ອຍ ຂັ້ນ 3</option>
                          </select>
                        ) : null}
                        {b.type === "photo" ? (
                          <>
                            <select
                              className="input-field"
                              value={b.align === "left" ? "left" : "right"}
                              disabled={!canEdit}
                              aria-label="ດ້ານຂອງກອບຮູບ"
                              onChange={(e) =>
                                editBlock(i, {
                                  align: e.target.value as LayoutBlock["align"],
                                })
                              }
                            >
                              <option value="left">ກອບຮູບຢູ່ເບື້ອງຊ້າຍ</option>
                              <option value="right">ກອບຮູບຢູ່ເບື້ອງຂວາ</option>
                            </select>
                            <p className="caption">
                              ຖ້າບລັອກຖັດໄປເປັນຊື່ເອກະສານ ຈະວາງຊື່ໄວ້ຂ້າງກອບຮູບ
                            </p>
                          </>
                        ) : null}
                        {b.type === "list" ? (
                          <textarea
                            className="input-field"
                            rows={3}
                            value={(b.items ?? []).join("\n")}
                            disabled={!canEdit}
                            placeholder="ແຖວລະລາຍການ"
                            onChange={(e) =>
                              editBlock(i, {
                                items: e.target.value.split("\n"),
                              })
                            }
                          />
                        ) : null}
                        {b.type === "signatures"
                          ? (b.columns ?? []).map((c, ci) => (
                              <div key={ci} className="row layout-sig">
                                <input
                                  className="input-field"
                                  value={c.title}
                                  disabled={!canEdit}
                                  aria-label="ຫົວຂໍ້"
                                  onChange={(e) =>
                                    editBlock(i, {
                                      columns: (b.columns ?? []).map((x, xi) =>
                                        xi === ci
                                          ? { ...x, title: e.target.value }
                                          : x,
                                      ),
                                    })
                                  }
                                />
                                <select
                                  className="input-field"
                                  value={c.image ?? ""}
                                  disabled={!canEdit}
                                  aria-label="ຮູບ"
                                  onChange={(e) =>
                                    editBlock(i, {
                                      columns: (b.columns ?? []).map((x, xi) =>
                                        xi === ci
                                          ? {
                                              ...x,
                                              image: e.target
                                                .value as typeof c.image,
                                            }
                                          : x,
                                      ),
                                    })
                                  }
                                >
                                  {Object.entries(IMAGE_LABEL).map(
                                    ([k, label]) => (
                                      <option key={k} value={k}>
                                        {label}
                                      </option>
                                    ),
                                  )}
                                </select>
                                <input
                                  className="input-field"
                                  value={c.name ?? ""}
                                  disabled={!canEdit}
                                  aria-label="ຊື່ໃຕ້ລາຍເຊັນ"
                                  onChange={(e) =>
                                    editBlock(i, {
                                      columns: (b.columns ?? []).map((x, xi) =>
                                        xi === ci
                                          ? { ...x, name: e.target.value }
                                          : x,
                                      ),
                                    })
                                  }
                                />
                              </div>
                            ))
                          : null}
                      </div>
                    ))}
                    {canEdit ? (
                      <div className="row" style={{ gap: "var(--space-2)" }}>
                        <select
                          className="input-field"
                          style={{ maxWidth: "16rem" }}
                          aria-label="ຊະນິດບລັອກທີ່ຈະເພີ່ມ"
                          value={newType}
                          onChange={(e) =>
                            setNewType(e.target.value as LayoutBlock["type"])
                          }
                        >
                          {Object.entries(BLOCK_LABEL).map(([k, label]) => (
                            <option key={k} value={k}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn-outline btn-sm"
                          disabled={
                            newType === "photo" &&
                            blocks.some((b) => b.type === "photo")
                          }
                          title={
                            newType === "photo" &&
                            blocks.some((b) => b.type === "photo")
                              ? "ມີກອບຮູບແລ້ວ"
                              : undefined
                          }
                          onClick={() =>
                            setBlocks((bs) => insertBlock(bs, newBlock(newType)))
                          }
                        >
                          + ເພີ່ມບລັອກ
                        </button>
                      </div>
                    ) : null}
                    {canEdit && newType === "photo" ? (
                      <p className="caption">
                        ກອບຮູບໃຊ້ຮູບທີ່ຜູ້ຮ້ອງແນບເປັນ ຮູບຕິດບັດ 3 × 4 — ຖ້າເອກະສານນີ້ຕ້ອງມີຮູບ
                        ໃຫ້ຕັ້ງເອກະສານຄັດຕິດ ຮູບຕິດບັດ 3 × 4 ເປັນບັງຄັບນຳ
                      </p>
                    ) : null}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>

        {canEdit && layout ? (
          <div className="modal__actions">
            {layout.active ? (
              <button
                type="button"
                className="btn-outline"
                disabled={busy !== ""}
                onClick={() =>
                  void run("disable", () =>
                    disableLayout(doc.id, layout.version),
                  )
                }
              >
                {busy === "disable" ? "ກຳລັງປິດ…" : "ປິດໃຊ້ (ກັບໄປໃຊ້ແບບເດີມ)"}
              </button>
            ) : null}
            {dirty ? (
              <button
                type="button"
                className="btn-outline"
                disabled={busy !== ""}
                onClick={() =>
                  void run("save", () =>
                    saveLayoutDraft(
                      doc.id,
                      blocks,
                      fields,
                      onePage,
                      layout.version,
                    ),
                  )
                }
              >
                {busy === "save" ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກ ແລະ ເບິ່ງຕົວຢ່າງ"}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-primary"
              disabled={
                busy !== "" ||
                dirty ||
                !hasDraft ||
                (layout.active && !layout.draft_changed)
              }
              title={dirty ? "ບັນທຶກກ່ອນຢືນຢັນ" : undefined}
              onClick={() =>
                void run("activate", () =>
                  activateLayout(doc.id, layout.version),
                )
              }
            >
              {busy === "activate" ? "ກຳລັງຢືນຢັນ…" : "ຢືນຢັນ ແລະ ເປີດໃຊ້"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * ข้อความ error จาก API เป็นภาษาอังกฤษของนักพัฒนา (`UPSTREAM_ERROR — …`) — แปลกรณีที่เจอบ่อยของ AI เป็นภาษาลาว
 * ที่บอกว่าต้องทำอะไรต่อ (ข้อความดิบยังเปิดดูได้ใต้ "ລາຍລະອຽດ")
 */
function layoutErrorLo(raw: string): string {
  if (raw.includes("deadline exceeded") || raw.includes("Timeout")) {
    return "AI ຕອບຊ້າເກີນໄປ — ກະລຸນາລອງໃໝ່ອີກຄັ້ງ (ໄຟລ໌ທີ່ມີຫຼາຍໜ້າອາດໃຊ້ເວລາດົນ)";
  }
  if (raw.includes("could not read this sample")) {
    return "AI ອ່ານໄຟລ໌ນີ້ແລ້ວ ແຕ່ສ້າງແມ່ແບບທີ່ໃຊ້ໄດ້ບໍ່ສຳເລັດ — ລອງໃໝ່ອີກຄັ້ງ ຫຼື ໃຊ້ໄຟລ໌ທີ່ຊັດກວ່າ/ມີໜ້ອຍໜ້າກວ່າ";
  }
  if (raw.startsWith("UPSTREAM_ERROR")) {
    return "ບໍລິການ AI ຂັດຂ້ອງຊົ່ວຄາວ — ກະລຸນາລອງໃໝ່ອີກຄັ້ງ";
  }
  return raw;
}
